import { TranslateService } from '@ngx-translate/core';
import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Subscription } from '../subscription';

import { ApolloSubscriptionService } from '../subscription.service';
import { takeUntil, take, switchMap, filter } from 'rxjs/operators';
import { Subject } from 'rxjs';
import {
  client,
  hostedFields,
  HostedFields,
  ThreeDSecure,
  threeDSecure,
} from 'braintree-web';
import { ThemeService } from '../../shared/services/theme.service';
import { ToastService } from '../../shared/modules/toast/toast.service';
import { LoggerService } from '../../shared/sdk';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { SubscriptionChange } from '../subscription-change';
import { OrganizationService } from '../../organizations/organization.service';

@Component({
  selector: 'app-subscription-add',
  templateUrl: './subscription-change.component.html',
  styleUrls: ['./subscription-change.component.css'],
})
export class SubscriptionChangeComponent implements OnInit, OnDestroy {
  @Input() subscription: SubscriptionChange;
  @Input() gateway: 'paypal' | 'creditcard';
  @Input() total: number;
  invalidFields: any = {};
  loading: { pay: boolean; token: boolean };
  locale = 'en';
  private braintreeClientInst: any;
  private braintreeThreeDSecureInst: any;
  private hostedFieldsInst: HostedFields;
  private onDestroy$: Subject<void> = new Subject();
  private themeDark = false;

  constructor(
    private activeModal: NgbActiveModal,
    private log: LoggerService,
    private orgService: OrganizationService,
    private subscriptionService: ApolloSubscriptionService,
    private themeService: ThemeService,
    private toastService: ToastService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loading = { token: true, pay: false };

    this.orgService.active
      .pipe(
        filter(org => !!org),
        takeUntil(this.onDestroy$)
      )
      .subscribe(({ locale }) => (this.locale = locale));

    this.themeService
      .isDark()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((isDark) => {
        this.themeDark = isDark;
      });

    this.subscriptionService.getClientToken().subscribe((token) => {
      client
        .create({
          authorization: token,
        })
        // https://developers.braintreepayments.com/guides/3d-secure/client-side/javascript/v3#render-a-checkout-page
        .then((clientInstance: any) => {
          this.braintreeClientInst = clientInstance;

          // Add 3ds
          // return this.braintreeClientInst.threeDSecure.create({
          return threeDSecure.create({
            // Documentation wants this but it doesn't work
            client: this.braintreeClientInst,
            version: 2,
          });
        })
        .then((threeDSecureInstance: ThreeDSecure) => {
          this.braintreeThreeDSecureInst = threeDSecureInstance;

          let color = 'black';
          let placeholerColor = 'lightgrey';

          if (this.themeDark) {
            color = 'white';
            placeholerColor = 'grey';
          }

          // https://developers.braintreepayments.com/guides/hosted-fields/styling/javascript/v3
          return hostedFields.create({
            client: this.braintreeClientInst,
            styles: {
              input: {
                'font-size': '15px',
                color,
              },
              '::-webkit-input-placeholder': {
                color: placeholerColor,
              },
              ':-moz-placeholder': {
                color: placeholerColor,
              },
              '::-moz-placeholder': {
                color: placeholerColor,
              },
              ':-ms-input-placeholder': {
                color: placeholerColor,
              },
            },
            fields: {
              number: {
                selector: '#cc-number',
                placeholder: '4111 1111 1111 1111',
              },
              cvv: {
                selector: '#cc-cvv',
                placeholder: '123',
              },
              expirationDate: {
                selector: '#cc-validity',
                placeholder: 'MM/YYYY',
              },
            },
          });
        })
        .then((hostedFieldsInst: HostedFields) => {
          this.hostedFieldsInst = hostedFieldsInst;
          this.loading.token = false;
        })
        .catch((err) => {
          this.log.log(err);
          this.loading.token = false;
        });
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.teardown();
  }

  teardown(): void {
    if (this.hostedFieldsInst) {
      try {
        this.hostedFieldsInst.teardown();
      } catch (e) {}
    }
  }

  onClose(): void {
    this.activeModal.dismiss();
  }

  onPay(): void {
    this.log.log('SubscriptionChange: onSave');
    this.loading.pay = true;
    this.invalidFields = {};

    this.hostedFieldsInst
      .tokenize()
      .then((payload: any) => {
        return this.braintreeThreeDSecureInst.verifyCard({
          onLookupComplete: (data: any, next: any) => {
            next();
          },
          amount: this.total,
          nonce: payload.nonce,
          bin: payload.details.bin,
        });
      })
      .then((payload: any) => {
        this.submitOrder(payload.nonce);
      })
      .catch((err: any) => {
        this.log.log(err);

        if (err.code === 'HOSTED_FIELDS_FIELDS_EMPTY') {
          this.invalidFields = {
            number: true,
            cvv: true,
            expirationDate: true,
          };
        } else if (err.code === 'HOSTED_FIELDS_FIELDS_INVALID') {
          this.invalidFields = {};

          err.details.invalidFieldKeys.forEach((elKey: string) => {
            this.invalidFields[elKey] = true;
          });
        }

        this.loading.pay = false;
      });
  }

  submitOrder(nonce: string): void {
    this.loading.pay = true;
    this.subscription.paymentMethodNonce = nonce;

    this.orgService.active
      .pipe(
        take(1),
        switchMap(({ id }) =>
          this.subscriptionService.change(id, this.subscription),
        ),
      )
      .subscribe(
        (sub: Subscription) => {
          this.teardown();
          this.activeModal.close(sub);
        },
        () => {
          this.onClose();
          this.toastService.error(
            this.translate.instant(
              'Error changin subscription, please try again or contact support.',
            ),
          );
        },
      )
      .add(() => (this.loading.pay = false));
  }
}
